import React, { useCallback, useEffect, useState } from 'react';

import { singletonHook } from 'react-singleton-hook';
import SocketIO, { Socket } from 'socket.io-client';
import { config } from './config';

type UseSocketIOClient = {
  socket: Socket | null,
  connected: boolean,
};

const useSocketIOClient = singletonHook(
  {
    socket: null as Socket | null,
    connected: false,
  },
  () => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [connected, setConnected] = useState(false);

    useEffect(() => {
      const password = window.localStorage.getItem('password') || window.prompt('Enter password');
      const socket = SocketIO(config.API_URL, {
        transports: ['websocket'],
        auth: {
          webPassword: password,
        },
      });

      const reRender = () => setConnected(socket.connected);
      const logDisconnect = (...args: any[]) => console.log('Socket disconnected:', args);
      socket.on('connect', reRender);
      socket.on('disconnect', logDisconnect);
      socket.on('disconnect', reRender);
      socket.once('hello', () => {
        window.localStorage.setItem('password', password || '');
      });

      setSocket(socket);

      return () => {
        setSocket(null);
        socket.close();
        socket.off('connect', reRender);
        socket.off('disconnect', reRender);
      };
    }, []);

    return React.useMemo(() => ({
      socket,
      connected,
    }), [socket, connected]);
  },
  {
    unmountIfNoConsumers: true,
  },
);

export const useSocketConnect = (io: UseSocketIOClient, callback: (socket: Socket) => void) => {
  useEffect(() => {
    if (io.connected) {
      callback(io.socket!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [io.socket, io.connected, callback]);
};

const useSocketEvent = (io: UseSocketIOClient, event: string, callback: (...args: any[]) => void) => {
  useEffect(() => {
    io.socket?.on(event, callback);
    return () => {
      io.socket?.off(event, callback);
    };
  }, [io, event, callback]);
};

export const App = () => {
  const [clients, setClients] = useState<any[]>([]);
  const [queues, setQueues] = useState<any[]>([]);

  const io = useSocketIOClient();

  useSocketConnect(
    io,
    React.useCallback((socket) => {
      socket.emit('presence:ident', { debug: true });
      socket.emit('presence:join');
      socket.emit('queues:join');
    }, []),
  );

  useSocketEvent(
    io,
    'presence:state',
    React.useCallback((state) => {
      setClients(state);
    }, []),
  );

  useSocketEvent(
    io,
    'queues:refetch',
    React.useCallback((state) => {
      console.log('queues:state', state);
      const seen = new Set();
      const newQueues = [...state, ...queues]
        .sort((a, b) => a.ts < b.ts ? 1 : -1)
        .filter((q) => {
          const key = `${q.queueId}:${q.newState}`;
          if (seen.has(key)) {
            return false;
          }
          seen.add(key);
          return true;
        })
        .slice(0, 20);
      console.log('queues:state now', newQueues);
      setQueues(newQueues);
    }, [queues]),
  );

  return (
    <div>
      io: {io.connected ? 'connected' : 'disconnected'}
      <br />
      connected:
      <ul>
        {clients.map((client) => <li key={client.id}>{JSON.stringify(client)}</li>)}
      </ul>
      queues:
      <ul>
        {queues.map((q) => <li key={q.queueId + ':' + q.newState}>{JSON.stringify(q)}</li>)}
      </ul>
      config: {JSON.stringify(config)}
    </div>
  );
};
